import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { ApiError } from "@/lib/apiClient";
import { useLicenseInfo, useActivateLicense, useFeatures, type ActivationResult, type FeatureDto, type FeatureVisibility } from "@/lib/license";
import { formatDateTime } from "@/lib/format";
import { SectionHeader } from "@/components/application/section-headers/section-headers";

function visBadge(v: FeatureVisibility): "success" | "warning" | "gray" {
  if (v === "ENABLED") return "success";
  if (v === "ADVERTISED") return "warning";
  return "gray"; // LOCKED / HIDDEN
}

function apiErrText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string } | string | null;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object" && body.message) return body.message;
  }
  return e instanceof Error ? e.message : String(e);
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-secondary py-3 sm:grid-cols-[200px_1fr] sm:gap-4">
      <dt className="text-sm font-medium text-tertiary">{k}</dt>
      <dd className="text-sm text-primary">{v}</dd>
    </div>
  );
}

export function LicensePage() {
  const { t } = useTranslation();
  const info = useLicenseInfo();
  const features = useFeatures();
  const activate = useActivateLicense();
  const [key, setKey] = useState("");
  const [result, setResult] = useState<ActivationResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { featureCounts, modules, planned } = useMemo(() => {
    const list = features.data?.features ?? [];
    const c = { ENABLED: 0, ADVERTISED: 0, LOCKED: 0 } as Record<string, number>;
    const byModule = new Map<string, FeatureDto[]>();
    for (const f of list) {
      c[f.visibility] = (c[f.visibility] ?? 0) + 1;
      const mod = f.module ?? "—";
      if (!byModule.has(mod)) byModule.set(mod, []);
      byModule.get(mod)!.push(f);
    }
    return {
      featureCounts: c,
      modules: [...byModule.entries()],
      planned: list.filter((f) => f.plannedVersion),
    };
  }, [features.data]);

  const submit = async () => {
    setErr(null);
    setResult(null);
    if (!key.trim()) return setErr(t("license.keyRequired"));
    try {
      setResult(await activate.mutateAsync(key.trim()));
    } catch (e) {
      // Backend vrací 400 se strukturovaným ActivationResult body pro neúspěšnou
      // (ale validní) aktivaci — zobraz ho jako výsledek, ne jako surovou chybu.
      if (e instanceof ApiError && e.body && typeof e.body === "object" && "success" in e.body) {
        setResult(e.body as ActivationResult);
      } else {
        setErr(apiErrText(e));
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("license.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("license.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {/* Aktivace klíče je akce, kvůli které se na tuhle stránku chodí — patří
          nahoru. Dole ji utopily dlouhé seznamy modulů a plánovaných funkcí. */}
      <div className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="text-md font-semibold text-primary">{t("license.activate")}</h2>
        <p className="mt-1 text-sm text-tertiary">{t("license.activateHint")}</p>
        {err && <div className="mt-3 rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
        {result && (
          <div className={`mt-3 rounded-lg px-3 py-2 text-sm ring-1 ${result.success ? "bg-success-primary text-success-primary ring-success" : "bg-error-primary text-error-primary ring-error"}`}>
            {result.success ? "✓ " : "✗ "}
            {result.message ?? (result.success ? t("license.activated") : t("license.activateFailed"))}
            {result.activatedModules && result.activatedModules.length > 0 && ` (${result.activatedModules.join(", ")})`}
          </div>
        )}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            className="flex-1 rounded-lg bg-primary px-3 py-2.5 font-mono text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand"
            placeholder="ETTS-1-CUSTOMER-MON+ADM-20261231-…"
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
          <Button color="primary" isDisabled={activate.isPending} onPress={() => void submit()}>
            {t("license.activate")}
          </Button>
        </div>
      </div>

      {info.isLoading && <LoadingIndicator />}
      {info.isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {info.data && (
        <div className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <dl>
            <Row k={t("license.customer")} v={`${info.data.customerName} (${info.data.customerId})`} />
            <Row
              k={t("license.tier")}
              v={<Badge type="pill-color" size="sm" color="brand">{info.data.tier}</Badge>}
            />
            <Row
              k={t("license.status")}
              v={
                <Badge type="pill-color" size="sm" color={info.data.valid ? "success" : "error"}>
                  {info.data.valid ? t("license.valid") : t("license.invalid")}
                </Badge>
              }
            />
            <Row k={t("license.validFrom")} v={formatDateTime(info.data.validFrom)} />
            <Row k={t("license.validTo")} v={formatDateTime(info.data.validTo)} />
            <Row
              k={t("license.modules")}
              v={
                <span className="flex flex-wrap gap-1">
                  {info.data.activeModules.map((m) => (
                    <Badge key={m} type="pill-color" size="sm" color="gray">{m}</Badge>
                  ))}
                </span>
              }
            />
            <Row
              k={t("license.features")}
              v={`${featureCounts.ENABLED} ${t("license.enabled")} · ${featureCounts.ADVERTISED} ${t("license.advertised")} · ${featureCounts.LOCKED} ${t("license.locked")}`}
            />
            {info.data.hasRuntimeActivation && (
              <Row k={t("license.runtimeExpiry")} v={formatDateTime(info.data.runtimeActivationExpiry)} />
            )}
          </dl>
        </div>
      )}

      {/* Feature stat boxy */}
      {features.data && (
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-xl bg-primary p-4 text-center ring-1 ring-secondary">
            <p className="text-display-xs font-semibold text-success-primary">{featureCounts.ENABLED}</p>
            <p className="text-xs text-tertiary">{t("license.enabled")}</p>
          </div>
          <div className="rounded-xl bg-primary p-4 text-center ring-1 ring-secondary">
            <p className="text-display-xs font-semibold text-warning-primary">{featureCounts.ADVERTISED}</p>
            <p className="text-xs text-tertiary">{t("license.advertised")}</p>
          </div>
          <div className="rounded-xl bg-primary p-4 text-center ring-1 ring-secondary">
            <p className="text-display-xs font-semibold text-secondary">{featureCounts.LOCKED}</p>
            <p className="text-xs text-tertiary">{t("license.locked")}</p>
          </div>
        </div>
      )}

      {/* Features by module */}
      {modules.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-md font-semibold text-primary">{t("license.featuresByModule")}</h2>
          <div className="flex flex-col gap-3">
            {modules.map(([mod, feats]) => (
              <details key={mod} className="rounded-xl bg-primary ring-1 ring-secondary">
                <summary className="flex cursor-pointer items-center gap-3 p-4">
                  <span className="text-md font-semibold text-primary">{mod}</span>
                  {feats[0]?.tier && (
                    <Badge type="pill-color" size="sm" color="brand">{feats[0].tier}</Badge>
                  )}
                  <span className="text-xs text-tertiary">
                    {feats.length} {t("license.featuresCount")}
                  </span>
                </summary>
                <div className="flex flex-col gap-2 border-t border-secondary p-4">
                  {feats.map((f) => (
                    <div key={f.id} className="flex flex-col gap-1 border-b border-secondary pb-2 last:border-0 last:pb-0">
                      <div className="flex items-center gap-2">
                        <Badge type="pill-color" size="sm" color={visBadge(f.visibility)}>{f.visibility}</Badge>
                        <span className="text-sm font-medium text-primary">{f.name ?? f.id}</span>
                        <span className="font-mono text-xs text-tertiary">{f.id}</span>
                      </div>
                      {f.shortDescription && <p className="text-xs text-tertiary">{f.shortDescription}</p>}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Planned features */}
      {planned.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-md font-semibold text-primary">{t("license.plannedFeatures")}</h2>
          <div className="flex flex-col gap-2">
            {planned.map((f) => (
              <div key={f.id} className="rounded-lg bg-primary p-4 ring-1 ring-secondary">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-primary">{f.name ?? f.id}</span>
                  <span className="font-mono text-xs text-tertiary">{f.id}</span>
                  <Badge type="pill-color" size="sm" color={visBadge(f.visibility)}>{f.visibility}</Badge>
                  <Badge type="pill-color" size="sm" color="gray">{f.plannedVersion}</Badge>
                </div>
                {f.advertisingText && <p className="mt-1 text-xs text-tertiary">{f.advertisingText}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
